import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

// ─── Types ────────────────────────────────────────────────────

export interface NoizaGmv {
  total_gmv: number
  total_orders: number
  avg_ticket: number
}

export interface NoizaGmvDaily {
  day: string
  gmv: number
  orders: number
}

export interface NoizaGmvByRestaurant {
  restaurant_id: string
  restaurant_name: string
  slug: string
  gmv: number
  orders: number
  avg_ticket: number
}

export interface NoizaChannelDistribution {
  channel: string
  orders: number
  pct: number
}

export interface NoizaSelfServicePct {
  self_service_pct: number
  waiter_pct: number
}

export interface NoizaActiveRestaurants {
  active_count: number
}

export interface NoizaUniqueCustomers {
  unique_count: number
}

export interface NoizaRepeatCustomers {
  repeat_count: number
  repeat_pct: number
}

export interface NoizaTopProduct {
  product_name: string
  restaurant_name: string
  restaurant_id?: string
  quantity: number
  revenue: number
}

export interface NoizaLowDay {
  restaurant_id: string
  restaurant_name: string
  day_of_week: string | number
  avg_gmv: number
  avg_orders: number
}

export interface NoizaLowHour {
  restaurant_id: string
  restaurant_name: string
  hour_of_day: string | number
  avg_gmv: number
  avg_orders: number
}

export interface NoizaSalesDaily {
  restaurant_id: string
  restaurant_name: string
  day: string
  gmv: number
  orders: number
}

export interface NoizaSalesHourly {
  restaurant_id: string
  restaurant_name: string
  hour: string | number
  gmv: number
  orders: number
}

export interface NoizaOrderValid {
  id: string
  created_at: string
  total: number
  status: string
  restaurant_id: string
  restaurant_name?: string
  customer_name?: string
}

export interface NoizaMetricsData {
  gmv: NoizaGmv
  gmvDaily: NoizaGmvDaily[]
  gmvByRestaurant: NoizaGmvByRestaurant[]
  channelDistribution: NoizaChannelDistribution[]
  selfServicePct: NoizaSelfServicePct
  activeRestaurants: number
  uniqueCustomers: number
  repeatCustomers: NoizaRepeatCustomers
  topProducts: NoizaTopProduct[]
  lowDays: NoizaLowDay[]
  lowHours: NoizaLowHour[]
  salesDaily: NoizaSalesDaily[]
  salesHourly: NoizaSalesHourly[]
  recentOrders: NoizaOrderValid[]
}

const DEFAULT_DATA: NoizaMetricsData = {
  gmv: { total_gmv: 0, total_orders: 0, avg_ticket: 0 },
  gmvDaily: [],
  gmvByRestaurant: [],
  channelDistribution: [],
  selfServicePct: { self_service_pct: 0, waiter_pct: 0 },
  activeRestaurants: 0,
  uniqueCustomers: 0,
  repeatCustomers: { repeat_count: 0, repeat_pct: 0 },
  topProducts: [],
  lowDays: [],
  lowHours: [],
  salesDaily: [],
  salesHourly: [],
  recentOrders: [],
}

export interface RestaurantOption {
  id: string
  name: string
  slug: string
}

export interface DateRange {
  from: string
  to: string
}

// Safe query helper — returns data or empty fallback, never throws
async function safeQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  fallback: T,
): Promise<T> {
  try {
    const { data, error } = await queryFn()
    if (error) {
      console.warn('[NOIZA Metrics] Query error:', error.message || error)
      return fallback
    }
    return data ?? fallback
  } catch (err) {
    console.warn('[NOIZA Metrics] Query exception:', err)
    return fallback
  }
}

// ─── Main Hook ─────────────────────────────────────────────

export function useNoizaMetrics(restaurantId?: string, dateRange?: DateRange) {
  const [data, setData] = useState<NoizaMetricsData>(DEFAULT_DATA)
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch restaurants list once
  useEffect(() => {
    async function fetchRestaurants() {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('id, name, slug')
          .eq('is_active', true)
          .order('name')
        if (!error && data) {
          setRestaurants(data)
        }
      } catch (err) {
        console.error('Error fetching restaurants:', err)
      }
    }
    fetchRestaurants()
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const filterById = restaurantId && restaurantId !== 'all'

      // Each query is individually safe — one failing won't break others
      const [
        gmvRaw,
        gmvDailyRaw,
        gmvByRestRaw,
        channelRaw,
        selfServiceRaw,
        activeRaw,
        uniqueRaw,
        repeatRaw,
        topProductsRaw,
        lowDaysRaw,
        lowHoursRaw,
        salesDailyRaw,
        salesHourlyRaw,
        ordersValidRaw,
      ] = await Promise.all([
        // v_noiza_gmv (single row)
        safeQuery(
          () => supabase.from('v_noiza_gmv').select('*').single(),
          null,
        ),
        // v_noiza_gmv_daily (no .order — sort client-side)
        safeQuery(() => supabase.from('v_noiza_gmv_daily').select('*'), []),
        // v_noiza_gmv_by_restaurant
        safeQuery(() => {
          let q = supabase.from('v_noiza_gmv_by_restaurant').select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
        // v_noiza_channel_distribution
        safeQuery(
          () => supabase.from('v_noiza_channel_distribution').select('*'),
          [],
        ),
        // v_noiza_self_service_pct (single row)
        safeQuery(
          () => supabase.from('v_noiza_self_service_pct').select('*').single(),
          null,
        ),
        // v_noiza_active_restaurants (single row)
        safeQuery(
          () =>
            supabase.from('v_noiza_active_restaurants').select('*').single(),
          null,
        ),
        // v_noiza_unique_customers (single row)
        safeQuery(
          () => supabase.from('v_noiza_unique_customers').select('*').single(),
          null,
        ),
        // v_noiza_repeat_customers (single row)
        safeQuery(
          () => supabase.from('v_noiza_repeat_customers').select('*').single(),
          null,
        ),
        // v_noiza_top_products (no .order — sort client-side)
        safeQuery(() => {
          let q = supabase.from('v_noiza_top_products').select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
        // v_noiza_low_days_by_restaurant
        safeQuery(() => {
          let q = supabase.from('v_noiza_low_days_by_restaurant').select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
        // v_noiza_low_hours_by_restaurant
        safeQuery(() => {
          let q = supabase.from('v_noiza_low_hours_by_restaurant').select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
        // v_noiza_sales_daily_by_restaurant (no .order — sort client-side)
        safeQuery(() => {
          let q = supabase.from('v_noiza_sales_daily_by_restaurant').select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
        // v_noiza_sales_hourly_by_restaurant (no .order — sort client-side)
        safeQuery(() => {
          let q = supabase
            .from('v_noiza_sales_hourly_by_restaurant')
            .select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
        // v_noiza_orders_valid
        safeQuery(() => {
          let q = supabase.from('v_noiza_orders_valid').select('*')
          if (filterById) q = q.eq('restaurant_id', restaurantId)
          return q
        }, []),
      ])

      // Client-side date filtering
      let filteredGmvDaily = Array.isArray(gmvDailyRaw) ? gmvDailyRaw : []
      let filteredSalesDaily = Array.isArray(salesDailyRaw) ? salesDailyRaw : []
      let filteredOrdersValid = Array.isArray(ordersValidRaw)
        ? ordersValidRaw
        : []

      if (dateRange && dateRange.from && dateRange.to) {
        const fromDate = new Date(dateRange.from).getTime()
        const toDate = new Date(dateRange.to).getTime() + 86400000

        filteredGmvDaily = filteredGmvDaily.filter((row: any) => {
          const d = new Date(row.day || row.date).getTime()
          return d >= fromDate && d <= toDate
        })

        filteredSalesDaily = filteredSalesDaily.filter((row: any) => {
          const d = new Date(row.day || row.date).getTime()
          return d >= fromDate && d <= toDate
        })

        filteredOrdersValid = filteredOrdersValid.filter((row: any) => {
          const d = new Date(row.created_at).getTime()
          return d >= fromDate && d <= toDate
        })
      }

      // Recalculate KPIs from filtered orders when date range is active
      const hasDateFilter = dateRange && dateRange.from && dateRange.to
      const computedGmv = hasDateFilter
        ? filteredOrdersValid.reduce(
            (sum: number, o: any) => sum + Number(o.total || o.amount || 0),
            0,
          )
        : Number(gmvRaw?.total_gmv ?? gmvRaw?.revenue ?? 0)
      const computedOrders = hasDateFilter
        ? filteredOrdersValid.length
        : Number(gmvRaw?.total_orders ?? gmvRaw?.orders ?? 0)
      const computedAvgTicket =
        computedOrders > 0 ? computedGmv / computedOrders : 0
      const computedUniqueCustomers = hasDateFilter
        ? new Set(
            filteredOrdersValid.map(
              (o: any) =>
                o.customer_name || o.customer || o.customer_phone || o.id,
            ),
          ).size
        : Number(
            uniqueRaw?.unique_count ||
              uniqueRaw?.count ||
              uniqueRaw?.unique_customers ||
              0,
          )

      setData({
        gmv: {
          total_gmv: computedGmv,
          total_orders: computedOrders,
          avg_ticket: computedAvgTicket,
        },
        gmvDaily: filteredGmvDaily
          .map((row: any) => ({
            day: row.day || row.date || '',
            gmv: Number(row.gmv || row.total_gmv || row.revenue || 0),
            orders: Number(
              row.orders || row.total_orders || row.order_count || 0,
            ),
          }))
          .sort((a: any, b: any) => String(a.day).localeCompare(String(b.day))),
        gmvByRestaurant: (Array.isArray(gmvByRestRaw) ? gmvByRestRaw : [])
          .map((row: any) => ({
            restaurant_id: String(row.restaurant_id || ''),
            restaurant_name: row.restaurant_name || row.name || 'Sin nombre',
            slug: row.slug || '',
            gmv: Number(row.gmv || row.total_gmv || row.revenue || 0),
            orders: Number(
              row.orders || row.total_orders || row.order_count || 0,
            ),
            avg_ticket: Number(row.avg_ticket || row.average_ticket || 0),
          }))
          .sort((a: any, b: any) => b.gmv - a.gmv),
        channelDistribution: (Array.isArray(channelRaw) ? channelRaw : []).map(
          (row: any) => ({
            channel: row.channel || row.order_channel || row.source || 'other',
            orders: Number(row.orders || row.order_count || row.total || 0),
            pct: Number(row.pct || row.percentage || 0),
          }),
        ),
        selfServicePct: {
          self_service_pct: Number(selfServiceRaw?.self_service_pct ?? 0),
          waiter_pct: Number(selfServiceRaw?.waiter_pct ?? 0),
        },
        activeRestaurants: Number(
          activeRaw?.active_count ||
            activeRaw?.count ||
            activeRaw?.active_restaurants ||
            0,
        ),
        uniqueCustomers: computedUniqueCustomers,
        repeatCustomers: {
          repeat_count: Number(
            repeatRaw?.repeat_count ||
              repeatRaw?.count ||
              repeatRaw?.repeat_customers ||
              0,
          ),
          repeat_pct: Number(
            repeatRaw?.repeat_pct ||
              repeatRaw?.pct ||
              repeatRaw?.percentage ||
              0,
          ),
        },
        topProducts: (Array.isArray(topProductsRaw) ? topProductsRaw : [])
          .map((row: any) => ({
            product_name: row.product_name || row.name || 'Producto',
            restaurant_name: row.restaurant_name || '',
            restaurant_id: String(row.restaurant_id || ''),
            quantity: Number(row.quantity || row.qty || 0),
            revenue: Number(row.revenue || row.gmv || 0),
          }))
          .sort((a: any, b: any) => b.revenue - a.revenue),
        lowDays: (Array.isArray(lowDaysRaw) ? lowDaysRaw : []).map(
          (row: any) => ({
            restaurant_id: String(row.restaurant_id || ''),
            restaurant_name: row.restaurant_name || '',
            day_of_week: row.day_of_week || row.day || '',
            avg_gmv: Number(row.avg_gmv || row.gmv || 0),
            avg_orders: Number(row.avg_orders || row.orders || 0),
          }),
        ),
        lowHours: (Array.isArray(lowHoursRaw) ? lowHoursRaw : []).map(
          (row: any) => ({
            restaurant_id: String(row.restaurant_id || ''),
            restaurant_name: row.restaurant_name || '',
            hour_of_day: row.hour_of_day || row.hour || '',
            avg_gmv: Number(row.avg_gmv || row.gmv || 0),
            avg_orders: Number(row.avg_orders || row.orders || 0),
          }),
        ),
        salesDaily: filteredSalesDaily
          .map((row: any) => ({
            restaurant_id: String(row.restaurant_id || ''),
            restaurant_name: row.restaurant_name || '',
            day: row.day || row.date || '',
            gmv: Number(row.gmv || row.revenue || 0),
            orders: Number(row.orders || row.order_count || 0),
          }))
          .sort((a: any, b: any) => String(a.day).localeCompare(String(b.day))),
        salesHourly: (Array.isArray(salesHourlyRaw) ? salesHourlyRaw : [])
          .map((row: any) => ({
            restaurant_id: String(row.restaurant_id || ''),
            restaurant_name: row.restaurant_name || '',
            hour: row.hour || row.hour_of_day || '',
            gmv: Number(row.gmv || row.revenue || 0),
            orders: Number(row.orders || row.order_count || 0),
          }))
          .sort((a: any, b: any) => Number(a.hour) - Number(b.hour)),
        recentOrders: filteredOrdersValid
          .map((row: any) => ({
            id: String(row.id || ''),
            created_at: row.created_at || '',
            total: Number(row.total || row.amount || 0),
            status: row.status || '',
            restaurant_id: String(row.restaurant_id || ''),
            restaurant_name: row.restaurant_name || '',
            customer_name: row.customer_name || row.customer || 'Cliente',
          }))
          .sort((a: any, b: any) =>
            String(b.created_at).localeCompare(String(a.created_at)),
          ),
      })
    } catch (err: any) {
      console.error('[NOIZA Platform] Error fetching metrics:', err)
      setError(err?.message || 'Error loading platform metrics')
    } finally {
      setLoading(false)
    }
  }, [restaurantId, dateRange])

  useEffect(() => {
    fetchAll()
    const interval = setInterval(fetchAll, 60000)
    return () => clearInterval(interval)
  }, [fetchAll])

  // Realtime subscription — refetch on any order change (insert/update/delete)
  useEffect(() => {
    const filterById = restaurantId && restaurantId !== 'all'
    const channelName = `noiza-metrics-orders-${restaurantId || 'all'}`

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          ...(filterById ? { filter: `restaurant_id=eq.${restaurantId}` } : {}),
        },
        () => {
          console.log('[NOIZA Metrics] Order change detected — refetching')
          fetchAll()
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [restaurantId, fetchAll])

  return { data, loading, error, refetch: fetchAll, restaurants }
}
